/**
 * Toaster — the app's single transient-feedback surface (Sonner-based).
 *
 * Placement (SCRUM-167) — TOP-CENTER, not Sonner's default bottom-right:
 *
 *   • Bottom is occupied on both personas. On mobile the consumer AppShell
 *     pins a bottom nav bar (`md:hidden`), and the admin product form has a
 *     sticky save bar — so a bottom toast lands on top of, or immediately
 *     above, the very control the user just pressed.
 *   • Centre rather than right: every surface puts the language switcher,
 *     theme toggle, account menu and Log out in the header's RIGHT corner, so
 *     `top-right` would cover them for the toast's whole lifetime. Centre also
 *     sits in the natural focal path after a failed submit.
 *   • The offset clears the header rather than floating over it
 *     (`--g-toast-offset-top`, styles/tailwind.css) so chrome stays usable
 *     while a toast is up.
 *
 * THEME (2026-08 fix) — Sonner shipped here as `theme="system"`, which reads
 * the OS colour-scheme. GardEn's theme is NOT the OS theme: `ThemeController`
 * resolves the UI store's `light | dark | system` onto `<html data-theme>`, so
 * a user who picked dark in-app while their OS is light got light-on-light
 * toasts — unreadable, and only in the one state nobody tests. The resolved
 * app theme is now passed explicitly, which also keeps the two in step when the
 * OS flips while the user is on "system".
 *
 * STYLING — the toast is a counter slip, not a floating pill. A semantic icon
 * in its own tinted disc carries the status (never colour alone), the title
 * sits in body ink at full contrast, the description in `--g-ink-2`, and the
 * whole surface is ruled with a hairline over `--g-elevated`. Deliberately no
 * thick coloured left border: a 4px status stripe is the default this design
 * language refuses.
 *
 * Everything else stays Sonner's default on purpose: `aria-live="polite"` with
 * no focus steal, swipe/click to dismiss, and a 4s auto-dismiss (inside the
 * 3-5s guidance) that `notify` lengthens for errors, which need reading time.
 *
 * Defaults are declared BEFORE `{...props}` so a caller can still override any
 * of them per mount.
 */
import { useEffect, useState } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  Info,
  LoaderCircle,
  OctagonAlert,
} from "lucide-react";
import { Toaster as SonnerToaster, type ToasterProps } from "sonner";

import { ICON_STROKE } from "@/lib/icons";
import { useUiStore } from "@/lib/ui-store";

const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";

/**
 * The concrete light/dark the app is CURRENTLY rendering, mirroring
 * `ThemeController`'s resolution so the toast layer can never disagree with
 * the page behind it. Kept local rather than exported from the controller
 * because the controller renders null and owns no state.
 */
function useResolvedTheme(): "light" | "dark" {
  const theme = useUiStore((state) => state.theme);
  const [systemDark, setSystemDark] = useState(false);

  useEffect(() => {
    if (theme !== "system" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(COLOR_SCHEME_QUERY);
    setSystemDark(mql.matches);
    const onChange = (event: MediaQueryListEvent): void => {
      setSystemDark(event.matches);
    };
    mql.addEventListener("change", onChange);
    return () => {
      mql.removeEventListener("change", onChange);
    };
  }, [theme]);

  if (theme === "dark") return "dark";
  if (theme === "light") return "light";
  return systemDark ? "dark" : "light";
}

const Toaster = (props: ToasterProps) => {
  const theme = useResolvedTheme();

  return (
    <SonnerToaster
      theme={theme}
      position="top-center"
      // One CSS var for both: it resolves against the app header's own `md`
      // breakpoint, which Sonner's 600px mobile cutoff would otherwise miss.
      offset={{ top: "var(--g-toast-offset-top)" }}
      mobileOffset={{ top: "var(--g-toast-offset-top)" }}
      className="toaster group"
      /*
       * Default icons for EVERY toast, not just the ones routed through
       * `notify`. ~140 call sites (most of them in admin) still call
       * `toast.error(...)` directly; without defaults here they would render
       * Sonner's built-in glyphs — a different icon system from the rest of the
       * app — or, if the set were left empty, an empty 32px status disc. These
       * are the same Lucide glyphs at the same `ICON_STROKE` that `notify`
       * passes explicitly, so both paths look identical.
       */
      icons={{
        success: <BadgeCheck size={17} strokeWidth={ICON_STROKE} />,
        error: <OctagonAlert size={17} strokeWidth={ICON_STROKE} />,
        warning: <AlertTriangle size={17} strokeWidth={ICON_STROKE} />,
        info: <Info size={17} strokeWidth={ICON_STROKE} />,
        loading: (
          <LoaderCircle
            size={17}
            strokeWidth={ICON_STROKE}
            className="g-spin"
            aria-hidden="true"
          />
        ),
      }}
      /*
       * NO `toastOptions.classNames` here on purpose.
       *
       * Tailwind utilities live in `@layer utilities`; Sonner's stylesheet is
       * unlayered, and unlayered CSS outranks layered CSS regardless of
       * specificity. Every class this component used to pass was silently
       * losing that cascade (measured: computed background stayed
       * `rgb(255,255,255)` with the class on the element). The whole toast
       * surface is styled from unlayered rules in `styles/tailwind.css`
       * instead, where it can actually win.
       */
      {...props}
    />
  );
};

export { Toaster };
